/**
 * Service per gestire l'estrazione testo da file .docx
 * Include fallback server-side ottimizzato per ridurre i falsi positivi
 * Priorità: client-side nativo > server-side solo per errori critici
 */

// Determina l'URL dell'API in base all'ambiente (allineato a openaiApi.js)
const getApiBaseUrl = () => {
  const env = process.env.REACT_APP_ENVIRONMENT;
  const dev = (process.env.REACT_APP_API_URL_DEVELOPMENT || '').replace(/["']/g, '');
  const pre = (process.env.REACT_APP_API_URL_PREPRODUCTION || '').replace(/["']/g, '');
  const prod = (process.env.REACT_APP_API_URL_PRODUCTION || '').replace(/["']/g, '');

  if (env === 'development' && dev) {
    console.log('🌐 Using development API URL:', `http://${dev}`);
    return `http://${dev}`;
  }
  if (env === 'preproduction' && pre) {
    console.log('🌐 Using preproduction API URL:', `https://${pre}`);
    return `https://${pre}`;
  }
  if (prod) {
    console.log('🌐 Using production API URL:', `https://${prod}`);
    return `https://${prod}`;
  }
  // Fallback: origin corrente
  console.warn('⚠️ API host non configurato, fallback a window.origin:', window.location.origin);
  return window.location.origin;
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Chiama il server per processare un file .docx quando mammoth fallisce
 * @param {string} base64Data - File .docx in formato base64
 * @param {string} filename - Nome del file
 * @returns {Promise<string>} Testo estratto
 */
export const processDocxOnServer = async (base64Data, filename) => {
  try {
    // Verifica dimensione file per evitare upload troppo grandi su mobile
    const fileSizeBytes = (base64Data.length * 3) / 4; // Approssimazione dimensione originale
    const maxSizeMB = isMobileDevice() ? 5 : 10; // Limite più basso per mobile
    
    if (fileSizeBytes > maxSizeMB * 1024 * 1024) {
      throw new Error(`File troppo grande per il processing server-side (max ${maxSizeMB}MB)`);
    }
    
    console.log(`🔧 Calling server-side DOCX processor for: ${filename} (${Math.round(fileSizeBytes/1024)}KB)`);
    console.log(`📡 API URL: ${API_BASE_URL}/api/process-docx`);
    
    const response = await fetch(`${API_BASE_URL}/api/process-docx`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file_data: base64Data,
        filename: filename
      }),
      // Timeout per evitare hang su mobile
      signal: AbortSignal.timeout(30000) // 30 secondi
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Server responded with status: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    
    if (result.success) {
      console.log('✅ Server-side DOCX processing successful');
      return result.extracted_text;
    } else {
      throw new Error(result.error_message || 'Server-side processing failed');
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      console.error('⏱️ Server-side DOCX processing timed out');
      throw new Error('Server processing timed out after 30 seconds');
    }
    
    console.error('❌ Server-side DOCX processing failed:', error);
    throw new Error(`Fallback server processing failed: ${error.message}`);
  }
};

/**
 * Verifica se il dispositivo è mobile per decidere quando usare il fallback
 * Aggiunge logging per debug
 */
export const isMobileDevice = () => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  const result = isIOS || isAndroid || isMobile;
  
  console.log('📱 Mobile device detection:', {
    isIOS,
    isAndroid,
    isMobile: result,
    userAgent: navigator.userAgent
  });
  
  return result;
};

/**
 * Verifica se il browser ha limitazioni note con mammoth
 * Riduce i falsi positivi per migliorare l'esperienza utente
 */
export const hasMammothLimitations = () => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);
  const isOldBrowser = !window.fetch || !window.Promise || !window.ArrayBuffer;
  
  // Controlla versioni Safari molto vecchie che hanno problemi con mammoth
  const safariVersion = navigator.userAgent.match(/Version\/(\d+)/);
  const isOldSafari = isSafari && safariVersion && parseInt(safariVersion[1]) < 14;
  
  // Controlla dispositivi iOS molto vecchi
  const iosVersion = navigator.userAgent.match(/OS (\d+)_/);
  const isOldIOS = isIOS && iosVersion && parseInt(iosVersion[1]) < 13;
  
  console.log('🔍 Checking mammoth limitations:', {
    isIOS,
    isSafari,
    isOldBrowser,
    isOldSafari,
    isOldIOS,
    userAgent: navigator.userAgent
  });
  
  // Solo dispositivi con limitazioni reali
  return isOldBrowser || isOldSafari || isOldIOS;
};
